import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { ArrowUpDown } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { alignmentsAtom } from '@/atoms/alignment.atom';
import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { reportTrackingAtom } from '@/atoms/report-progress.atom';
import { execute } from '@/graphql/execute';
import type { LayersQuery } from '@/graphql/graphql';
import { useFeature } from '@/hooks/useFeature';
import {
  DEFAULT_MIN_COVERAGE_PCT,
  deriveStationVolumes,
  MAX_CROSSFALL_TOLERANCE_PCT,
  MIN_CROSSFALL_BAND_WIDTH_M,
  MIN_CROSSFALL_TOLERANCE_PCT,
  refineStationVolumes,
  stationVolumesDefaults,
  stationVolumesPayload,
  stationVolumesZodShape,
  type StationVolumesFormValues,
  type StationVolumesInitialParams,
} from '@/lib/stationVolumes/dialogModel';
import { reportLocaleFor, SUPPORTED_REPORT_LOCALES } from '@/lib/reportLocales';
import { expectationDefaults, expectationPayload, expectationZodShape, refineExpectation } from '@/lib/surfaceExpectation';
import { useDialog } from '@/providers/DialogProvider';
import { startSurfaceDifference } from '@/queries/surfaceDifference.query';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExpectationFields } from '@/components/ExpectationFields';
import { matplotlibRampOptions } from '@/lib/colormaps';
import { defaultRampName } from '@/lib/cutFillConvention';
import { StationVolumesFields } from '@/components/StationVolumesFields';
import { PC_METHODS, SurfacePicker } from '@/components/SurfacePicker';

type LayerData = LayersQuery['layers'][number];

/**
 * The ramp picker's options, oriented so that CUT is red (T4). A surface
 * difference is `dz = top − bottom`, so its cut side is the negative one —
 * which is why these are the plain matplotlib spellings (`Spectral`, `RdBu`)
 * rather than the `_r` ones a deviation run uses. One list, in
 * `@/lib/colormaps`; `jet` is gone from it on purpose.
 */
const COLORMAPS = matplotlibRampOptions('surfaceDifference');

const schema = z
  .object({
    name: z.string().min(1),
    topLayerId: z.string().min(1),
    topSurfaceIds: z.array(z.string()),
    topPcMethod: z.enum(PC_METHODS),
    bottomLayerId: z.string().min(1),
    bottomSurfaceIds: z.array(z.string()),
    bottomPcMethod: z.enum(PC_METHODS),
    resolution: z.number().positive(),
    colormap: z.string(),
    fillHoles: z.number().int().min(0),
    exportDxf: z.boolean(),
    exportTif: z.boolean(),
    boundaryLayerId: z.string().default(''),
    generateReport: z.boolean(),
    reportLocale: z.enum(SUPPORTED_REPORT_LOCALES),
    ...stationVolumesZodShape,
    ...expectationZodShape,
  })
  .superRefine((d, ctx) => {
    if (d.topLayerId === d.bottomLayerId && !(d.topSurfaceIds.length > 0 && d.bottomSurfaceIds.length > 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bottomLayerId'], message: 'sameLayer' });
    }
    refineStationVolumes(d, ctx);
    refineExpectation(d, ctx);
  });
export type SurfaceDifferenceFormData = z.infer<typeof schema>;

export const isSurfaceCandidate = (l: LayerData) =>
  l.type === 'POINTCLOUD' ||
  l.type === 'VECTOR' ||
  (l.type === 'MODEL' && ['LANDXML', 'DXF', 'IFC'].includes((l.metadata as any)?.type ?? ''));

// The pre-fill shape now lives with the shared station model; re-exported so
// existing importers (`StationVolumeDock.tsx`) keep working.
export type { StationVolumesInitialParams };

interface Props {
  projectId: string;
  candidateLayers: LayerData[];
  initialTopLayerId?: string;
  initialBottomLayerId?: string;
  initialValues?: Partial<SurfaceDifferenceFormData>;
  initialStationParams?: StationVolumesInitialParams;
}

export function RunSurfaceDifferenceDialog({
  projectId,
  candidateLayers,
  initialTopLayerId,
  initialBottomLayerId,
  initialValues,
  initialStationParams,
}: Props) {
  const { t, i18n } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const [, setTrackers] = useAtom(deviationTrackingAtom);
  const [, setReportTrackers] = useAtom(reportTrackingAtom);
  const allLayers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);
  // The station-volumes section stays visible on every tier — a reader has to
  // be able to see what the analysis offers before deciding to pay for it — but
  // its controls read as muted and the enable checkbox answers a click with the
  // upgrade dialog rather than the HTML `disabled` attribute (CLAUDE.md).
  const stationGate = useFeature('analysis.stationVolumes');
  // The thickness/conformance question is its own PRO gate, independent of
  // the parent analysis (owner's ruling) — same shape as `stationGate` above.
  const expectationGate = useFeature('analysis.surfaceExpectation');
  // `assertReportOptions` refuses `generate: true` below Pro and would fail the
  // whole run start, so the box answers a locked click with the upgrade dialog.
  const reportGate = useFeature('report.pdf');

  const top0 = initialTopLayerId ?? candidateLayers[0]?.id ?? '';
  const bottom0 = initialBottomLayerId ?? candidateLayers.find((l) => l.id !== top0)?.id ?? '';
  const nameOf = (id: string) => candidateLayers.find((l) => l.id === id)?.name ?? '';

  // Not a form field (no UI control in this dialog yet) — carried through to
  // the mutation payload unchanged so a re-run preserves the coverage
  // threshold the previous run used.
  const initialMinCoveragePct = initialStationParams?.minCoveragePct ?? DEFAULT_MIN_COVERAGE_PCT;

  const form = useForm<SurfaceDifferenceFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: `${nameOf(top0)} − ${nameOf(bottom0)}`.trim(),
      topLayerId: top0,
      topSurfaceIds: [],
      topPcMethod: 'min',
      bottomLayerId: bottom0,
      bottomSurfaceIds: [],
      bottomPcMethod: 'min',
      resolution: 0.25,
      colormap: defaultRampName('surfaceDifference'),
      fillHoles: 0,
      exportDxf: false,
      exportTif: true,
      boundaryLayerId: '',
      generateReport: false,
      reportLocale: reportLocaleFor(i18n.language),
      // Includes the gate on `stationVolumesEnabled`: the dock's "Re-run with
      // new parameters" always supplies `initialStationParams`, so without it a
      // locked user would land on a fully live, submittable station section
      // (review round 1, item 1).
      ...stationVolumesDefaults(initialStationParams, alignments, stationGate.allowed),
      // NONE by default. Note `...initialValues` is spread AFTER this, so a
      // caller-supplied `initialValues.expectMode` COULD in principle still
      // seed a non-NONE value here even while locked — this default alone
      // does not rule that out. What actually rules it out is
      // `ExpectationFields`' own gate-lapse effect, which resets
      // `expectMode` to NONE whenever the gate is closed, including on the
      // very first render (fix round 2, item 1 — an earlier version of this
      // comment claimed the spread order made this unreachable, which was
      // not true).
      ...expectationDefaults(),
      ...initialValues,
    },
  });

  const watchedTopId = form.watch('topLayerId');
  const watchedBottomId = form.watch('bottomLayerId');
  const boundaryCandidates = useMemo(
    () => allLayers.filter((l) => l.type === 'VECTOR' && l.id !== watchedTopId && l.id !== watchedBottomId),
    [allLayers, watchedTopId, watchedBottomId],
  );
  const watchedBoundaryLayerId = form.watch('boundaryLayerId');
  useEffect(() => {
    if (watchedBoundaryLayerId && !boundaryCandidates.some((l) => l.id === watchedBoundaryLayerId)) {
      form.setValue('boundaryLayerId', '');
    }
  }, [watchedBoundaryLayerId, boundaryCandidates, form]);

  const generateReport = form.watch('generateReport');

  const svValues: StationVolumesFormValues = {
    stationVolumesEnabled: form.watch('stationVolumesEnabled'),
    alignmentKey: form.watch('alignmentKey'),
    stationInterval: form.watch('stationInterval'),
    swathLeft: form.watch('swathLeft'),
    swathRight: form.watch('swathRight'),
    swathLinked: form.watch('swathLinked'),
    stationFromText: form.watch('stationFromText'),
    stationToText: form.watch('stationToText'),
    offsetBin: form.watch('offsetBin'),
    crossfallEnabled: form.watch('crossfallEnabled'),
    crossfallLeftEnabled: form.watch('crossfallLeftEnabled'),
    crossfallRightEnabled: form.watch('crossfallRightEnabled'),
    crossfallLeftFrom: form.watch('crossfallLeftFrom'),
    crossfallLeftTo: form.watch('crossfallLeftTo'),
    crossfallRightFrom: form.watch('crossfallRightFrom'),
    crossfallRightTo: form.watch('crossfallRightTo'),
    crossfallLinked: form.watch('crossfallLinked'),
    crossfallTolerancePct: form.watch('crossfallTolerancePct'),
  };
  const resolution = form.watch('resolution');
  const alignment = alignments.get(svValues.alignmentKey);
  // One derivation feeds both the section's messages and the Run button, so
  // they cannot disagree about what is blocking (spec §10.4).
  const svDerived = useMemo(
    () => deriveStationVolumes(alignment, svValues, resolution),
    // Each field individually: `svValues` is a fresh object every render.
    // `swathLinked` is deliberately absent — the link is a UI affordance the
    // derivation never reads.
    [
      alignment,
      resolution,
      svValues.stationVolumesEnabled,
      svValues.stationInterval,
      svValues.swathLeft,
      svValues.swathRight,
      svValues.stationFromText,
      svValues.stationToText,
      svValues.offsetBin,
      // The crossfall fields the derivation reads. `crossfallLinked` is
      // deliberately absent for the same reason `swathLinked` is — it is a UI
      // affordance the derivation never looks at.
      svValues.crossfallEnabled,
      svValues.crossfallLeftEnabled,
      svValues.crossfallRightEnabled,
      svValues.crossfallLeftFrom,
      svValues.crossfallLeftTo,
      svValues.crossfallRightFrom,
      svValues.crossfallRightTo,
      svValues.crossfallTolerancePct,
    ],
  );

  /** `StationVolumesFields` reports what the user typed; the form decides what
   *  it means. One `setValue` per field rather than a loop over
   *  `Object.entries`, which loses the key→value type pairing and would need a
   *  cast to put it back together. */
  const applyStation = useCallback(
    (patch: Partial<StationVolumesFormValues>) => {
      if (patch.stationVolumesEnabled !== undefined)
        form.setValue('stationVolumesEnabled', patch.stationVolumesEnabled);
      if (patch.alignmentKey !== undefined) form.setValue('alignmentKey', patch.alignmentKey);
      if (patch.stationInterval !== undefined) form.setValue('stationInterval', patch.stationInterval);
      if (patch.swathLeft !== undefined) form.setValue('swathLeft', patch.swathLeft);
      if (patch.swathRight !== undefined) form.setValue('swathRight', patch.swathRight);
      if (patch.swathLinked !== undefined) form.setValue('swathLinked', patch.swathLinked);
      if (patch.stationFromText !== undefined) form.setValue('stationFromText', patch.stationFromText);
      if (patch.stationToText !== undefined) form.setValue('stationToText', patch.stationToText);
      if (patch.offsetBin !== undefined) form.setValue('offsetBin', patch.offsetBin);
      // Linked swath mirroring lives with the form, not the field component:
      // the component reports one side, the form decides what that means for
      // the other. Kept here so the deviation dialog gets it for free.
      if (patch.swathLeft !== undefined && form.getValues('swathLinked')) form.setValue('swathRight', patch.swathLeft);
      if (patch.swathRight !== undefined && form.getValues('swathLinked')) form.setValue('swathLeft', patch.swathRight);
      if (patch.crossfallEnabled !== undefined) form.setValue('crossfallEnabled', patch.crossfallEnabled);
      if (patch.crossfallLeftEnabled !== undefined)
        form.setValue('crossfallLeftEnabled', patch.crossfallLeftEnabled);
      if (patch.crossfallRightEnabled !== undefined)
        form.setValue('crossfallRightEnabled', patch.crossfallRightEnabled);
      if (patch.crossfallLeftFrom !== undefined) form.setValue('crossfallLeftFrom', patch.crossfallLeftFrom);
      if (patch.crossfallLeftTo !== undefined) form.setValue('crossfallLeftTo', patch.crossfallLeftTo);
      if (patch.crossfallRightFrom !== undefined) form.setValue('crossfallRightFrom', patch.crossfallRightFrom);
      if (patch.crossfallRightTo !== undefined) form.setValue('crossfallRightTo', patch.crossfallRightTo);
      if (patch.crossfallLinked !== undefined) form.setValue('crossfallLinked', patch.crossfallLinked);
      if (patch.crossfallTolerancePct !== undefined)
        form.setValue('crossfallTolerancePct', patch.crossfallTolerancePct);
      // Linked bands mirror the way the linked swath does, and for the same
      // reason: the component reports ONE side, the form decides what that
      // means for the other, so all three dialogs get it from one place.
      if (form.getValues('crossfallLinked')) {
        if (patch.crossfallLeftFrom !== undefined) form.setValue('crossfallRightFrom', patch.crossfallLeftFrom);
        if (patch.crossfallLeftTo !== undefined) form.setValue('crossfallRightTo', patch.crossfallLeftTo);
        if (patch.crossfallRightFrom !== undefined) form.setValue('crossfallLeftFrom', patch.crossfallRightFrom);
        if (patch.crossfallRightTo !== undefined) form.setValue('crossfallLeftTo', patch.crossfallRightTo);
      }
    },
    [form],
  );

  /**
   * Whether this submission will chain a PDF report. The mutation payload and
   * the progress tracker MUST agree: a tracker registered for a report the
   * server was never asked to produce polls until the tab is closed. The
   * report always carries the whole-overlap summary; station volumes, when
   * the run has them, add the corridor pages. Reads `reportGate.allowed`
   * live rather than trusting the form: a subscription can lapse while the
   * dialog is open.
   */
  const willChainReport = (vars: SurfaceDifferenceFormData) => reportGate.allowed && vars.generateReport;

  const swap = () => {
    const v = form.getValues();
    form.setValue('topLayerId', v.bottomLayerId);
    form.setValue('bottomLayerId', v.topLayerId);
    form.setValue('topSurfaceIds', v.bottomSurfaceIds);
    form.setValue('bottomSurfaceIds', v.topSurfaceIds);
    form.setValue('topPcMethod', v.bottomPcMethod);
    form.setValue('bottomPcMethod', v.topPcMethod);
  };

  const mutation = useMutation({
    mutationFn: (vars: SurfaceDifferenceFormData) =>
      execute(startSurfaceDifference, {
        input: {
          top: {
            layerId: vars.topLayerId,
            surfaceIds: vars.topSurfaceIds.length ? vars.topSurfaceIds : undefined,
            pcMethod: vars.topPcMethod,
          },
          bottom: {
            layerId: vars.bottomLayerId,
            surfaceIds: vars.bottomSurfaceIds.length ? vars.bottomSurfaceIds : undefined,
            pcMethod: vars.bottomPcMethod,
          },
          name: vars.name || undefined,
          boundaryLayerId: vars.boundaryLayerId || undefined,
          params: {
            resolution: vars.resolution,
            colormap: vars.colormap,
            fillHoles: vars.fillHoles,
            exportDxf: vars.exportDxf,
            exportTif: vars.exportTif,
          },
          // Re-read the gate here, not the form flag: it can lapse while the
          // dialog is open and the mutation costs real compute, so the gate
          // has the last word (mirrors `stationGate.allowed` just below).
          // `expectationPayload` returns `null` for mode NONE; `?? undefined`
          // turns that into an ABSENT key so an ordinary run's payload is
          // byte-identical to today's, not `expectation: null`.
          expectation: expectationGate.allowed ? (expectationPayload(vars) ?? undefined) : undefined,
          // `stationGate.allowed` is re-read here, not trusted from the form:
          // the flag can be seeded true by `initialStationParams` and a
          // subscription can lapse while the dialog is open. The mutation costs
          // real compute, so the last word belongs to the gate.
          //
          // `svDerived` is reused rather than recomputed (R14/D17): it was
          // derived from the same form state `vars` carries, so a second
          // `deriveStationVolumes` call here could only ever disagree.
          stationVolumes: stationGate.allowed
            ? stationVolumesPayload(vars, svDerived, initialMinCoveragePct)
            : undefined,
          // The chained report prints the whole-overlap summary, plus the
          // corridor pages when the run carries station volumes (spec §10.6).
          reportOptions: willChainReport(vars)
            ? { generate: true, title: vars.name || undefined, notes: undefined, locale: vars.reportLocale }
            : undefined,
        },
      }),
    onSuccess: (data, vars) => {
      const batchId = data.startSurfaceDifference;
      const topName = nameOf(vars.topLayerId);
      const toastId = toast.loading(t('surfaceDifference.runningToast', { name: vars.name }), { duration: Infinity });
      // Reuse the deviation progress hook: it resolves the toast when a new
      // layer with metadata.derivedFrom === topLayerId appears (postprocess
      // sets derivedFrom = topLayerId for SURFACE_DIFFERENCE layers).
      const existing = allLayers.filter((l) => (l.metadata as any)?.derivedFrom === vars.topLayerId).map((l) => l.id);
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: vars.topLayerId,
          sourceLayerName: vars.name || topName,
          existingDerivedLayerIds: existing,
          kind: 'surfaceDifference' as const,
        },
      ]);
      // Register the chained report so `useReportProgress` raises the download
      // toast when the PDF lands — the same thing the deviation dialog does.
      // Same predicate as the payload above: no request, no tracker.
      if (willChainReport(vars)) {
        setReportTrackers((prev) => [...prev, { toastId: undefined, batchId, projectId, title: vars.name }]);
      }
      queryClient.invalidateQueries({ queryKey: ['surfaceDifferenceJobs', projectId] });
      dismissActiveDialog();
    },
    onError: (err: Error) => toast.error(`${t('surfaceDifference.failedToStart')}: ${err.message}`),
  });

  const sorted = useMemo(() => [...candidateLayers].sort((a, b) => a.name.localeCompare(b.name)), [candidateLayers]);
  if (sorted.length === 0) return <div className='p-4 text-sm'>{t('surfaceDifference.noCandidates')}</div>;

  // Defence in depth (fix round 2, item 1): if validation ever fails while
  // Run is clickable — including a future regression, not just today's
  // fixed tick/600/untick sequence — this makes sure the click still DOES
  // something visible instead of a silent no-op. Maps the schema's custom
  // issue `message`s (tags like 'intervalRange', not literal text) back to
  // real copy where one exists; anything else falls back to the generic
  // "could not start" toast.
  const onInvalid = (errors: FieldErrors<SurfaceDifferenceFormData>) => {
    const messages: Record<string, string> = {
      sameLayer: t('surfaceDifference.sameLayer'),
      alignmentRequired: t('stationVolumes.dialog.alignmentRequired'),
      swathZero: t('stationVolumes.dialog.swathZero'),
      intervalRange: t('stationVolumes.errors.intervalRange'),
      swathRange: t('stationVolumes.errors.swathRange'),
      offsetBinRange: t('stationVolumes.errors.offsetBinRange'),
      crossfallBand: t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M }),
      crossfallWidth: t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M }),
      crossfallSwath: t('stationVolumes.errors.crossfallSwath'),
      crossfallNoBand: t('stationVolumes.errors.crossfallNoBand'),
      crossfallTolerance: t('stationVolumes.errors.crossfallTolerance', {
        min: MIN_CROSSFALL_TOLERANCE_PCT,
        max: MAX_CROSSFALL_TOLERANCE_PCT,
      }),
      thicknessRequired: t('expectation.errors.thicknessRequired'),
      toleranceRequired: t('expectation.errors.toleranceRequired'),
      surfaceRequired: t('expectation.errors.surfaceRequired'),
      sameSurface: t('expectation.errors.sameSurface'),
      toleranceNegative: t('expectation.errors.toleranceNegative'),
      failAreaPctRange: t('expectation.errors.failAreaPctRange'),
    };
    const first = Object.values(errors).find(
      (e): e is { message?: string } => !!e && typeof e === 'object' && 'message' in e,
    );
    const text = (first?.message && messages[first.message]) || t('surfaceDifference.failedToStart');
    toast.error(text);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((v) => mutation.mutate(v), onInvalid)}
        // `min-w-0`: this form is a grid/flex item of the caller's `DialogContent`
        // (this component doesn't render its own `DialogContent`, so it can't set
        // `overflow-hidden`/`max-w-[calc(100vw-2rem)]` there directly — see
        // `app/src/components/ui/dialog.tsx`); without `min-w-0` the item refuses
        // to shrink below its content's natural width at narrow dialog sizes.
        // `overflow-hidden` clips any residual horizontal overflow at the outermost
        // level this component controls.
        className='flex max-h-[80vh] min-w-0 flex-col overflow-hidden'>
        <DialogHeader>
          <DialogTitle>{t('surfaceDifference.title')}</DialogTitle>
          <DialogDescription>{t('surfaceDifference.description')}</DialogDescription>
        </DialogHeader>
        <div className='flex-1 min-w-0 overflow-x-hidden overflow-y-auto py-4 flex flex-col gap-4'>
          <SurfacePicker form={form} prefix='top' layers={sorted} label={t('surfaceDifference.top')} />
          <div className='flex justify-center'>
            <Button type='button' variant='ghost' size='sm' onClick={swap}>
              <ArrowUpDown className='size-4 mr-1' /> {t('surfaceDifference.swap')}
            </Button>
          </div>
          <SurfacePicker form={form} prefix='bottom' layers={sorted} label={t('surfaceDifference.bottom')} />
          {/*
            The orientation is the single easiest thing to get wrong here, and
            getting it wrong INVERTS cut and fill silently — every region, both
            directions, and the reported volumes with them. `Δz = top − bottom`
            with `Δz > 0` meaning fill, so a design check needs the DESIGN on
            top. The labels used to read "Top / existing" and "Bottom / design",
            which said the opposite and produced confidently backwards results.
            The orientation guard in `expectation.py::assert_orientation` does
            not cover this: it only fires when a positive thickness expectation
            is set, and a plain difference run has none.
          */}
          <p className='text-xs text-muted-foreground'>{t('surfaceDifference.orientationHint')}</p>
          {form.formState.errors.bottomLayerId?.message === 'sameLayer' && (
            <p className='text-sm text-destructive'>{t('surfaceDifference.sameLayer')}</p>
          )}
          <ExpectationFields
            form={form}
            allowSurfaces
            layers={sorted}
            gate={expectationGate}
            effectiveResolutionM={resolution}
          />
          <FormField
            control={form.control}
            name='resolution'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('surfaceDifference.resolution')}</FormLabel>
                <FormControl>
                  <NumericField value={field.value} onChange={field.onChange} step='0.05' min='0.01' />
                </FormControl>
                <FormDescription>{t('surfaceDifference.resolutionHelp')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='colormap'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('surfaceDifference.colormap')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {/* Whatever a RE-RUN carried in is appended if it is no
                        longer on the list (T4 dropped `jet` and the cut-blue
                        orientation). A `Select` whose value is not among its
                        items renders EMPTY, which reads as "no ramp set" and
                        invites the user to pick a different one than the run
                        they are repeating actually used. */}
                    {(COLORMAPS.some((c) => c.value === field.value) || !field.value
                      ? COLORMAPS
                      : [...COLORMAPS, { value: field.value, label: field.value }]
                    ).map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='boundaryLayerId'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('boundary.label')}</FormLabel>
                <Select
                  onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                  value={field.value || '__none__'}>
                  <FormControl>
                    <SelectTrigger className='w-full'>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value='__none__'>{t('boundary.none')}</SelectItem>
                    {boundaryCandidates.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        <span className='min-w-0 flex-1 truncate'>{l.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>{t('boundary.help')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <StationVolumesFields
            values={svValues}
            onChange={applyStation}
            resolution={resolution}
            derived={svDerived}
            gate={stationGate}
            referenceLayerId={watchedTopId}
          />
          <FormField
            control={form.control}
            name='fillHoles'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('surfaceDifference.fillHoles')}</FormLabel>
                <FormControl>
                  <NumericField value={field.value} onChange={field.onChange} step='1' min='0' />
                </FormControl>
                <FormDescription>{t('surfaceDifference.fillHolesHelp')}</FormDescription>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='exportDxf'
            render={({ field }) => (
              <FormItem className='flex items-center gap-2'>
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel className='!mt-0'>{t('surfaceDifference.exportDxf')}</FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='exportTif'
            render={({ field }) => (
              <FormItem className='flex items-center gap-2'>
                <FormControl>
                  <Checkbox data-testid='sd-export-tif' checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel className='!mt-0'>{t('surfaceDifference.exportTif')}</FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('surfaceDifference.outputName')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* PDF report opt-in (spec §10.6): the whole-overlap summary, plus the
              station volumes section when the run has an alignment. */}
          <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
            <div className={`flex items-center gap-2 ${reportGate.allowed ? '' : 'opacity-50'}`}>
              <Checkbox
                id='sd-report'
                data-testid='sd-report'
                checked={generateReport && reportGate.allowed}
                onCheckedChange={(c) => {
                  if (!reportGate.allowed) {
                    reportGate.promptUpgrade();
                    return;
                  }
                  form.setValue('generateReport', !!c, { shouldDirty: true });
                }}
              />
              {/* `htmlFor` on a button is valid — a button is a labelable
                  element — so the copy toggles the box like a native one. */}
              <label htmlFor='sd-report' className='text-sm'>
                {t('report.alsoGenerate')}
              </label>
            </div>
            {generateReport && reportGate.allowed && (
              <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>{t('report.surfaceDifferenceHint')}</p>
            )}
            {stationGate.allowed && svValues.stationVolumesEnabled && generateReport && (
              <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                {t('stationVolumes.dialog.reportIncludesSection')}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid='sv-submit' type='submit' disabled={mutation.isPending || svDerived.blocked}>
            {mutation.isPending ? t('surfaceDifference.starting') : t('surfaceDifference.run')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
